import { z } from 'zod';
import { refersTo } from '../common/refers-to';

/**
 * Crossing from immoOS into a linked application (IMM-751.2.1).
 *
 * The caller names a `targetId` and nothing else. It is an opaque key into the
 * deployment's configured application map — NEVER a URL: a caller-supplied
 * redirect would turn the handoff into a way to send a member's identity
 * anywhere, which is the whole risk this mechanism exists to bound.
 */
export const MintAppHandoffPathParamsSchema = z.object({ workspaceId: z.uuid() });

export const MintAppHandoffBodySchema = z.object({
  targetId: z.string().trim().min(1).max(64),
});

export type MintAppHandoffBody = z.infer<typeof MintAppHandoffBodySchema>;

/**
 * The address to send the browser to. Absolute, built wholly from configuration,
 * with the raw token in the URL's **fragment** (`#immo_handoff=…`).
 *
 * The fragment is not decorative. A query parameter is sent to the target's
 * server — so it lands in its access logs — and rides the `Referer` of every
 * third-party asset the landing page loads. A fragment never leaves the browser.
 *
 * This is the only time the raw token exists outside the browser's address bar:
 * it is not stored, logged, or carried on any event.
 */
export const MintAppHandoffResponseSchema = z.object({
  redirectUrl: z.url(),
});

export type MintAppHandoffResponse = z.infer<typeof MintAppHandoffResponseSchema>;

/**
 * The raw value the receiving application read out of the redirect's fragment
 * (IMM-751.2.2).
 *
 * There is no workspace or target in the body, and that is the point: both come
 * from the presenting key's principal. A body field naming either would be a
 * claim the caller makes about itself, so any key could assert any workspace or
 * any target and the binding would protect nothing.
 */
export const ExchangeAppHandoffBodySchema = z.object({
  token: z.string().min(1).max(128),
});

export type ExchangeAppHandoffBody = z.infer<typeof ExchangeAppHandoffBodySchema>;

/**
 * Three ids and nothing else. The crossing says WHO is acting; reading anything
 * about them — the member's profile, the location's address — is a separate
 * call with its own grant.
 *
 * `standortId` is nullable because a member may have no primary Standort, and it
 * is the Standort the token RECORDED at mint time rather than a fresh
 * resolution: a member who moved mid-crossing arrives where they started.
 */
export const ExchangeAppHandoffResponseSchema = z.object({
  memberId: refersTo('member', z.uuid()),
  workspaceId: z.uuid(),
  standortId: refersTo('organization', z.uuid()).nullable(),
});

export type ExchangeAppHandoffResponse = z.infer<typeof ExchangeAppHandoffResponseSchema>;

/**
 * The application the „Druckmaterial" entry point is for (IMM-751.4).
 *
 * `LINKED_APPLICATIONS` is a per-workspace ARRAY — the shape exists so a
 * workspace can eventually have more than one — so neither the navigation row
 * nor the page may take "the first one". A workspace whose first configured
 * application is something else would otherwise show a Druckmaterial row that
 * mints a crossing into the wrong application: silently, and only for that
 * customer.
 *
 * One well-known id rather than a generic mechanism, deliberately. This page's
 * route segment, its label and every string on it say Druckmaterial; a second
 * linked application needs its own entry point, and the operator screen that
 * would manage several is the deferred follow-up PRD (parent Resolved
 * Decision 6).
 */
export const PRINT_MATERIAL_TARGET_ID = 'print-material';

export const ListLinkedApplicationsPathParamsSchema = z.object({ workspaceId: z.uuid() });

/**
 * What immoOS's own web app is told about a workspace's approved applications
 * (IMM-751.4).
 *
 * **No URL, deliberately.** The browser never learns where a crossing goes: the
 * redirect target is minted server-side, so nothing the client holds can steer
 * one. Returning it to save a round trip would put a redirect target in the
 * browser, which is one refactor away from a caller-supplied one — the single
 * control this whole feature rests on.
 *
 * Three independent flags, and they must stay three: whether an application is
 * here at all decides whether the entry point CAN exist, `entryPointVisible`
 * decides whether it is OFFERED, and `released` decides whether its action
 * WORKS. Collapsing any two makes a state unreachable — "ready later",
 * "wired up but not announced" and "not for this customer" each need their own
 * surface (IMM-778.1).
 */
export const LinkedApplicationSummarySchema = z.object({
  targetId: z.string(),
  released: z.boolean(),
  entryPointVisible: z.boolean(),
});

export type LinkedApplicationSummary = z.infer<typeof LinkedApplicationSummarySchema>;

export const ListLinkedApplicationsResponseSchema = z.object({
  applications: z.array(LinkedApplicationSummarySchema),
});

export type ListLinkedApplicationsResponse = z.infer<typeof ListLinkedApplicationsResponseSchema>;
